import { useEffect, useRef } from 'react'

import { postMessageTypes } from '@/configuration/pages/ConfigurationPage/ThemeConfiguration/constants'

import { flattenColors, flattenImages } from './helpers'

const usePreviewMessaging = ({ colors, images, previewUrl, activePreviewSection }) => {
  const iframeRef = useRef(null)
  const iframeReady = useRef(false)
  const lastThemeRef = useRef(null)
  const lastSectionRef = useRef(null)
  const functionsRef = useRef({})

  const getOrigin = () => {
    if (!previewUrl) return null
    try {
      return new URL(previewUrl).origin
    } catch {
      return null
    }
  }

  const sendMessage = (type, payload) => {
    const iframe = iframeRef.current
    const origin = getOrigin()
    if (!iframe?.contentWindow || !origin) return

    iframe.contentWindow.postMessage({ type, payload }, origin)
  }

  const sendThemeUpdate = themeData => {
    sendMessage(postMessageTypes.themeUpdate, themeData)
  }

  const sendPreviewSection = section => {
    sendMessage(postMessageTypes.previewSection, { section })
  }

  functionsRef.current = { getOrigin, sendThemeUpdate, sendPreviewSection }

  useEffect(() => {
    const handleMessage = event => {
      const expectedOrigin = functionsRef.current.getOrigin()
      if (expectedOrigin && event.origin !== expectedOrigin) return

      const { type } = event.data || {}
      if (type === postMessageTypes.iframeReady) {
        iframeReady.current = true
        const flatTheme = { ...flattenColors(colors), ...flattenImages(images) }
        functionsRef.current.sendThemeUpdate(flatTheme)
        if (activePreviewSection) {
          functionsRef.current.sendPreviewSection(activePreviewSection)
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [colors, images, activePreviewSection])

  useEffect(() => {
    iframeReady.current = false
  }, [previewUrl])

  useEffect(() => {
    if (!iframeReady.current) return

    const flatTheme = { ...flattenColors(colors), ...flattenImages(images) }
    const themeString = JSON.stringify(flatTheme)

    if (lastThemeRef.current === themeString) return
    lastThemeRef.current = themeString

    functionsRef.current.sendThemeUpdate(flatTheme)
  }, [colors, images])

  useEffect(() => {
    if (!iframeReady.current || !activePreviewSection) return
    if (lastSectionRef.current === activePreviewSection) return
    lastSectionRef.current = activePreviewSection

    functionsRef.current.sendPreviewSection(activePreviewSection)
  }, [activePreviewSection])

  return {
    iframeRef,
    sendThemeUpdate,
    sendPreviewSection,
  }
}

export default usePreviewMessaging
